import { useState, useContext } from 'react';
import {
    Container, Box, TextField, MenuItem, Select, FormControl,
    InputLabel, Button, Typography, Alert, CircularProgress, Paper,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import Nav from '../../components/nav';
import AuthContext from '../../components/authContext';
import FirebaseHelper from '../../services/firestore/index.ts';

const ACTION_RESET_PASSWORD = 'reset_password';
const ACTION_GET_LINK = 'get_link';
const ACTION_GET_VERIFY_LINK = 'get_verify_link';

const UserManagementPage = () => {
    const authContext = useContext(AuthContext);

    const [identifier, setIdentifier] = useState('');
    const [action, setAction] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetLink, setResetLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const canSubmit = identifier.trim() !== '' && action !== '' &&
        (action !== ACTION_RESET_PASSWORD || newPassword.trim() !== '');

    const handleButtonClick = () => {
        if (action === ACTION_RESET_PASSWORD) {
            setConfirmOpen(true);
        } else {
            handleConfirm();
        }
    };

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setLoading(true);
        setResult(null);
        setError(null);
        setResetLink('');

        try {
            if (action === ACTION_RESET_PASSWORD) {
                const res = await FirebaseHelper.resetPassword(identifier.trim(), newPassword);
                setResult(`Password updated successfully for UID: ${res.uid}`);
            } else if (action === ACTION_GET_LINK) {
                const res = await FirebaseHelper.getPasswordResetLink(identifier.trim());
                setResetLink(res.resetLink);
                setResult(`Password reset link generated for UID: ${res.uid}`);
            } else if (action === ACTION_GET_VERIFY_LINK) {
                const res = await FirebaseHelper.getEmailVerificationLink(identifier.trim());
                setResetLink(res.verificationLink);
                setResult(`Email verification link generated for UID: ${res.uid}`);
            }
        } catch (err) {
            const msg = err?.message || err?.error || 'An error occurred';
            const status = err?.status ? `[${err.status}] ` : '';
            setError(`${status}${msg}`);
        } finally {
            setLoading(false);
        }
    };

    if (!authContext.isAdmin) {
        return (
            <>
                <Nav />
                <Container maxWidth="sm" sx={{ mt: 12 }}>
                    <Typography>Not Authorized</Typography>
                </Container>
            </>
        );
    }

    return (
        <>
            <Nav />
            <Container maxWidth="sm" sx={{ mt: 12 }}>
                <Paper elevation={2} sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom>
                        User Management
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <TextField
                            label="User ID or Email"
                            variant="outlined"
                            fullWidth
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder="Enter user ID or email address"
                        />

                        <FormControl fullWidth>
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={action}
                                label="Action"
                                onChange={(e) => {
                                    setAction(e.target.value);
                                    setResult(null);
                                    setError(null);
                                    setResetLink('');
                                }}
                            >
                                <MenuItem value={ACTION_RESET_PASSWORD}>Reset Password</MenuItem>
                                <MenuItem value={ACTION_GET_LINK}>Get Password Reset Link</MenuItem>
                                <MenuItem value={ACTION_GET_VERIFY_LINK}>Get Email Verification Link</MenuItem>
                            </Select>
                        </FormControl>

                        {action === ACTION_RESET_PASSWORD && (
                            <TextField
                                label="New Password"
                                variant="outlined"
                                fullWidth
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                            />
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleConfirm}
                            disabled={!canSubmit || loading}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
                        </Button>

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}

                        {result && (
                            <Alert severity="success">{result}</Alert>
                        )}

                        {resetLink && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {action === ACTION_GET_VERIFY_LINK ? 'Email Verification Link:' : 'Password Reset Link:'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={resetLink}
                                    InputProps={{ readOnly: true }}
                                    multiline
                                    size="small"
                                    inputProps={{ onFocus: (e) => e.target.select() }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Password Reset</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reset the password for <strong>{identifier}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirm} variant="contained" color="error">
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserManagementPage;
